import type {
  IAuditLog,
  IIssue,
  IIssueComment,
  IIssueImportCorrectionRow,
  IIssueImportInspect,
  IIssueImportPreview,
  IIssueImportResult,
  IIssueWorkLog,
  IPaginatedResponse,
  IssueImportHeaderMapping,
  IssueImportMapping,
  IssueDependencyType,
  IssueLinkType,
  IssuePriority,
  IssueType,
} from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const base = (projectId: string) => `/api/v1/projects/${projectId}/issues`;

export interface IssueInput {
  name: string;
  /** `null` explicitly clears the description on an existing issue; `undefined` leaves it unset at creation. */
  description?: string | null;
  type: IssueType;
  /** `null` explicitly clears the priority on an existing issue; `undefined` leaves it unset at creation. */
  priority?: IssuePriority | null;
  /** `null` explicitly clears the module on an existing issue; `undefined` leaves it unset at creation. */
  moduleId?: string | null;
  /** Must reference an Approved Change Request. `null` explicitly clears it on an existing issue; `undefined` leaves it unset at creation. */
  changeRequestId?: string | null;
  parentIssueId?: string;
  /** `null` explicitly clears the owner on an existing issue; `undefined` leaves it unset at creation. */
  ownerId?: string | null;
  assigneeIds?: string[];
  /** `null` explicitly clears the estimate on an existing issue; `undefined` leaves it unset at creation. */
  originalEstimate?: number | null;
  /** `null` explicitly clears the due date on an existing issue; `undefined` leaves it unset at creation. */
  dueDate?: string | null;
  /** `null` explicitly clears the start date on an existing issue; `undefined` leaves it unset at creation. */
  startDate?: string | null;
  flagged?: boolean;
  flagNote?: string;
  expedited?: boolean;
  isMilestoneTrigger?: boolean;
  /** `null` explicitly clears the payment milestone on an existing issue; `undefined` leaves it unset at creation. */
  paymentMilestoneId?: string | null;
  /** `null` explicitly clears the funding milestone on an existing issue; `undefined` leaves it unset at creation. */
  fundingMilestoneId?: string | null;
  /** `null` explicitly clears the sprint on an existing issue; `undefined` leaves it unset at creation. */
  sprintId?: string | null;
}

export async function fetchIssues(projectId: string): Promise<IIssue[]> {
  const res = await apiFetch(base(projectId));
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch issues'));
  return res.json() as Promise<IIssue[]>;
}

export async function fetchIssue(projectId: string, issueId: string): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch issue'));
  return res.json() as Promise<IIssue>;
}

export async function createIssue(projectId: string, data: IssueInput): Promise<IIssue> {
  const res = await apiFetch(base(projectId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create issue'));
  return res.json() as Promise<IIssue>;
}

export async function updateIssue(projectId: string, issueId: string, data: Partial<IssueInput>): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update issue'));
  return res.json() as Promise<IIssue>;
}

export async function transitionIssue(projectId: string, issueId: string, toStep: string): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ toStep }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to move issue to the new step'));
  return res.json() as Promise<IIssue>;
}

export async function addIssueLink(
  projectId: string,
  issueId: string,
  linkedIssueId: string,
  linkType: IssueLinkType,
  dependencyType?: IssueDependencyType,
): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linkedIssueId, linkType, dependencyType }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add link'));
  return res.json() as Promise<IIssue>;
}

export async function removeIssueLink(projectId: string, issueId: string, linkId: string): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/links/${linkId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to remove link'));
  return res.json() as Promise<IIssue>;
}

export async function followIssue(projectId: string, issueId: string): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/follow`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to follow issue'));
  return res.json() as Promise<IIssue>;
}

export async function unfollowIssue(projectId: string, issueId: string): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/follow`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to unfollow issue'));
  return res.json() as Promise<IIssue>;
}

export async function fetchIssueComments(projectId: string, issueId: string): Promise<IIssueComment[]> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/comments`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch comments'));
  return res.json() as Promise<IIssueComment[]>;
}

export async function addIssueComment(projectId: string, issueId: string, body: string): Promise<IIssueComment> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add comment'));
  return res.json() as Promise<IIssueComment>;
}

export async function updateIssueComment(
  projectId: string,
  issueId: string,
  commentId: string,
  body: string,
): Promise<IIssueComment> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/comments/${commentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update comment'));
  return res.json() as Promise<IIssueComment>;
}

export async function fetchIssueWorkLogs(projectId: string, issueId: string): Promise<IIssueWorkLog[]> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/work-logs`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch work logs'));
  return res.json() as Promise<IIssueWorkLog[]>;
}

export async function addIssueWorkLog(
  projectId: string,
  issueId: string,
  data: { hours: number; description?: string; date: string },
): Promise<IIssueWorkLog> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/work-logs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add work log'));
  return res.json() as Promise<IIssueWorkLog>;
}

export async function updateIssueWorkLog(
  projectId: string,
  issueId: string,
  workLogId: string,
  data: { hours?: number; description?: string | null; date?: string },
): Promise<IIssueWorkLog> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/work-logs/${workLogId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update work log'));
  return res.json() as Promise<IIssueWorkLog>;
}

// ── History (audit trail) ──────────────────────────────────────────────────────
export async function fetchIssueHistory(
  projectId: string,
  issueId: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<IPaginatedResponse<IAuditLog>> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  const res = await apiFetch(`${base(projectId)}/${issueId}/history?${params.toString()}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch issue history'));
  return res.json() as Promise<IPaginatedResponse<IAuditLog>>;
}

// ── Import from Excel ─────────────────────────────────────────────────────────
export async function fetchIssueImportTemplate(projectId: string): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`${base(projectId)}/import/template`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to download the import template'));
  const match = res.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : 'issues-import-template.xlsx';
  return { blob: await res.blob(), filename };
}

// The caller's own remembered header->field mapping for this project (§Map columns) — null if
// never saved. Server-side, per (user, project), replacing what used to be a localStorage cache.
export async function fetchIssueImportMapping(projectId: string): Promise<IssueImportHeaderMapping | null> {
  const res = await apiFetch(`${base(projectId)}/import/mapping`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch the saved column mapping'));
  return res.json() as Promise<IssueImportHeaderMapping | null>;
}

export async function saveIssueImportMapping(projectId: string, mapping: IssueImportHeaderMapping): Promise<void> {
  const res = await apiFetch(`${base(projectId)}/import/mapping`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mapping }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to save the column mapping'));
}

export interface IssueImportOptions {
  /** Worksheet to read; omitted, the API auto-picks the first sheet whose headers cover Name + Type. */
  sheet?: string;
  /** Explicit column → field mapping; omitted, the API auto-detects columns from the header row. */
  mapping?: IssueImportMapping;
}

// The upload endpoints (inspect/validate/commit) don't take plain JSON — they take
// multipart/form-data, because a real file can't go inside a JSON body. FormData is the
// browser's way of building that kind of request. No Content-Type header here either — the
// browser sets the boundary itself; `sheet` and `mapping` ride along as text fields.
function importFormData(file: File, opts: IssueImportOptions = {}): FormData {
  const formData = new FormData();
  formData.append('file', file);
  if (opts.sheet !== undefined) formData.append('sheet', opts.sheet);
  if (opts.mapping) formData.append('mapping', JSON.stringify(opts.mapping));
  return formData;
}

export async function fetchIssueImportCorrections(
  projectId: string,
  rows: IIssueImportCorrectionRow[],
): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(`${base(projectId)}/import/corrections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to build the corrections file'));
  const match = res.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : 'issues-import-corrections.xlsx';
  return { blob: await res.blob(), filename };
}

export async function inspectIssueImport(projectId: string, file: File, sheet?: string): Promise<IIssueImportInspect> {
  const res = await apiFetch(`${base(projectId)}/import/inspect`, { method: 'POST', body: importFormData(file, { sheet }) });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to read the import file'));
  return res.json() as Promise<IIssueImportInspect>;
}

export async function validateIssueImport(projectId: string, file: File, opts: IssueImportOptions = {}): Promise<IIssueImportPreview> {
  const res = await apiFetch(`${base(projectId)}/import/validate`, { method: 'POST', body: importFormData(file, opts) });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to validate the import file'));
  return res.json() as Promise<IIssueImportPreview>;
}

export async function commitIssueImport(
  projectId: string,
  file: File,
  importAnyway: number[],
  updateRows: number[],
  opts: IssueImportOptions = {},
): Promise<IIssueImportResult> {
  const params = new URLSearchParams();
  if (importAnyway.length > 0) params.set('importAnyway', importAnyway.join(','));
  if (updateRows.length > 0) params.set('updateRows', updateRows.join(','));
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiFetch(`${base(projectId)}/import/commit${query}`, { method: 'POST', body: importFormData(file, opts) });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to import issues'));
  return res.json() as Promise<IIssueImportResult>;
}

// ── Soft-delete (deactivate / activate) ──────────────────────────────────────
// Only accessible to users with issue:deactivate permission (Tech Lead, Project Manager).
export async function deactivateIssue(projectId: string, issueId: string): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to deactivate issue'));
  return res.json() as Promise<IIssue>;
}

export async function activateIssue(projectId: string, issueId: string): Promise<IIssue> {
  const res = await apiFetch(`${base(projectId)}/${issueId}/activate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to activate issue'));
  return res.json() as Promise<IIssue>;
}
